/**
 * facets.js — Filtres et tri (collection / recherche).
 *
 * Un seul formulaire GET : déplacé entre sidebar desktop et panneau mobile inline.
 * Mobile collection : pills → panneau déroulant sous les pills (maquette).
 */

(() => {
  'use strict';

  const root = document.querySelector('[data-facets-root]');
  if (!root) return;

  const form = root.querySelector('[data-facets-form]') || document.getElementById('FacetsForm');
  if (!form) return;

  const sectionId = root.closest('.shopify-section')?.id.replace('shopify-section-', '');
  const desktop = window.matchMedia('(min-width: 990px)');
  const sidebar = root.querySelector('.collection-layout__aside, [data-facets-sidebar]');
  const mobileInline = root.querySelector('[data-facets-mobile-inline]');
  const drawerSlot = document.querySelector('[data-facets-mobile]');

  let controller = null;

  /* lmdh 12/10/2026 (lot AP, U72) : un filtre coche au clavier rendait le focus a la page (la case est detruite quand le
     formulaire est remplace) et le nouveau nombre de modeles n etait pas annonce. On note le dernier geste (clavier ou
     pointeur), on rend le focus a l element equivalent apres le remplacement, et une region vivante annonce le compte.
     Onglets du telephone : aria-selected et aria-controls (le panneau ouvert est un tabpanel), fleches entre onglets. */
  let clavier = false;
  root.addEventListener('keydown', () => { clavier = true; }, true);
  root.addEventListener('pointerdown', () => { clavier = false; }, true);
  let annonce = root.querySelector('[data-facets-annonce]');
  if (!annonce) {
    annonce = document.createElement('p');
    annonce.className = 'visually-hidden';
    annonce.setAttribute('aria-live', 'polite');
    annonce.setAttribute('data-facets-annonce', '');
    root.prepend(annonce);
  }
  const annoncer = () => {
    // apres l observateur de la grille (collection.liquid), qui reecrit le compte d une famille de coloris
    window.setTimeout(() => {
      const compte = root.querySelector('[data-facets-count]');
      const texte = compte ? compte.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!texte) return;
      annonce.textContent = '';
      window.setTimeout(() => { annonce.textContent = texte; }, 60);
    }, 120);
  };
  const visible = (el) => !!el && el.isConnected && el.getClientRects().length > 0;
  const cleFocus = () => {
    const a = document.activeElement;
    if (!a || a === document.body || !root.contains(a)) return null;
    const facet = a.closest('details.facet');
    const id = facet ? facet.getAttribute('data-facet-id') : null;
    if (a.matches('input.facet__input')) return { sorte: 'case', name: a.name, value: a.value, id };
    if (a.matches('input[type="number"]') && a.id) return { sorte: 'id', cible: a.id, id };
    if (a.matches('.facet__famille[data-famille]')) return { sorte: 'famille', cle: a.getAttribute('data-famille'), id };
    if (a.closest('[data-facets-chips], .facets__head')) return { sorte: 'puce', id: null };
    return null;
  };
  const rendreFocus = (cle) => {
    if (!cle) return;
    const a = document.activeElement;
    if (a && a !== document.body && root.contains(a) && visible(a)) return;
    let el = null;
    if (cle.sorte === 'case') el = [...form.querySelectorAll('input.facet__input')].find((i) => i.name === cle.name && i.value === cle.value);
    else if (cle.sorte === 'id') el = document.getElementById(cle.cible);
    else if (cle.sorte === 'famille') el = [...form.querySelectorAll('.facet__famille[data-famille]')].find((b) => b.getAttribute('data-famille') === cle.cle);
    // au telephone, la case vit dans un panneau : au clavier, on le rouvre ; au doigt, il se referme comme avant
    if (el && !visible(el) && !desktop.matches && mobileInline && clavier && cle.id) {
      const d = [...form.querySelectorAll('details.facet')].find((x) => x.getAttribute('data-facet-id') === cle.id);
      if (d) {
        d.open = true;
        syncMobileTabs();
      }
    }
    if (!visible(el) && cle.id) el = [...root.querySelectorAll('[data-facet-tab]')].find((t) => t.getAttribute('data-facet-tab') === cle.id);
    if (!visible(el)) el = root.querySelector('[data-facets-chips] .chip');
    if (!visible(el)) {
      el = root.querySelector('[data-facets-count]');
      if (el) el.setAttribute('tabindex', '-1');
    }
    if (visible(el)) el.focus({ preventScroll: true });
  };
  const roles = () => {
    form.querySelectorAll('details.facet').forEach((d) => {
      const corps = d.querySelector('.facet__body');
      if (!corps) return;
      const tab = [...root.querySelectorAll('[data-facet-tab]')].find((t) => t.getAttribute('data-facet-tab') === d.getAttribute('data-facet-id'));
      if (!desktop.matches && mobileInline && tab && tab.id) {
        corps.setAttribute('role', 'tabpanel');
        corps.setAttribute('aria-labelledby', tab.id);
      } else {
        corps.removeAttribute('role');
        corps.removeAttribute('aria-labelledby');
      }
    });
  };

  const placeForm = () => {
    const target = desktop.matches
      ? sidebar
      : mobileInline || drawerSlot || sidebar;
    if (!target) return;
    if (form.parentElement !== target) target.appendChild(form);

    if (!desktop.matches && mobileInline) {
      form.querySelectorAll('details.facet').forEach((details) => {
        details.open = false;
      });
      root.querySelectorAll('[data-facet-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', 'false');
      });
    }
    roles();
  };

  /* lmdh 30/09/2026 (rapport 334, C2 et C10) : filtre Coloris. Rangee de familles en tete (un toucher coche toutes
     les valeurs de la famille, une seule mise a jour), gris et neutres d abord ; filtre Taille range par largeur. */
  const PRIORITE = ['grigio antracite', 'grigio', 'grigio chiaro', 'nero', 'beige'];
  const nomDe = (li) => li?.querySelector('.facet__label, .facet__swatch-name')?.textContent.trim() || '';
  // lmdh 30/09/2026 (retours du proprietaire) : un coloris hors table n est jamais perdu, il tombe dans « Autres coloris »
  const AUTRES = { cle: 'autres', nom: 'Altri colori', couleur: '#E8E4DC' };
  const famillesDe = () => [...window.LMH.FAMILLES, AUTRES];
  const familleDe = (nom) => window.LMH.familleColoris(nom) || AUTRES;
  const rangColoris = (nom) => {
    const p = PRIORITE.indexOf(nom.toLowerCase());
    if (p >= 0) return p;
    const f = window.LMH?.familleColoris?.(nom);
    return 10 + (f ? window.LMH.FAMILLES.indexOf(f) : window.LMH?.FAMILLES?.length || 99);
  };
  const ranger = (liste, rang) => {
    [...liste.children]
      .map((li, i) => ({ li, i, r: rang(nomDe(li)) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .forEach(({ li }) => liste.appendChild(li));
  };
  const entreesFamille = (facet, f) =>
    [...facet.querySelectorAll('input.facet__input')].filter(
      (i) => !i.disabled && familleDe(nomDe(i.closest('li'))) === f
    );
  const enrichirFiltres = () => {
    form.querySelectorAll('details.facet').forEach((facet) => {
      const liste = facet.querySelector('.facet__values');
      if (!liste || liste.dataset.enrichi) return;
      liste.dataset.enrichi = 'true';
      const etiquette = (facet.dataset.facetLabel || '').toLowerCase();
      if (etiquette === 'taille' || etiquette === 'misura') {
        ranger(liste, (nom) => { const m = nom.match(/\((\d+)/); return m ? Number(m[1]) : 9999; });
        return;
      }
      if (!facet.classList.contains('facet--swatch') || !window.LMH?.FAMILLES) return;
      ranger(liste, rangColoris);
      // lmdh 30/09/2026 (retours du proprietaire) : le filtre ne montre plus que les couleurs generales (« Gris »
      // coche tous les gris). La liste detaillee reste dans le formulaire, cachee : ce sont ses cases qui partent
      // dans l adresse de la page.
      const presentes = famillesDe().filter((f) => entreesFamille(facet, f).length > 0);
      if (!presentes.length) return;
      facet.classList.add('facet--familles');
      const rangee = document.createElement('div');
      rangee.className = 'facet__familles';
      rangee.setAttribute('role', 'group');
      rangee.setAttribute('aria-label', 'Colori');
      presentes.forEach((f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'facet__famille';
        b.dataset.famille = f.cle;
        const entrees = entreesFamille(facet, f);
        const n = entrees.filter((i) => i.checked).length;
        b.setAttribute('aria-pressed', n === 0 ? 'false' : n === entrees.length ? 'true' : 'mixed');
        b.innerHTML = '<span class="facet__famille-dot" style="background:' + f.couleur + '"></span>';
        b.append(f.nom);
        rangee.appendChild(b);
      });
      liste.before(rangee);
      // le compteur du titre compte des couleurs, plus des valeurs (« 27 » pour un seul geste sur « Gris »)
      const compteur = facet.querySelector('.facet__active-count');
      if (compteur) compteur.textContent = String(presentes.filter((f) => entreesFamille(facet, f).some((i) => i.checked)).length);
    });
  };

  // lmdh 30/09/2026 (retours du proprietaire) : le rappel des filtres actifs parle en couleurs generales, une puce
  // par couleur (« Gris × »), au lieu d une puce par valeur ou d une puce « Coloris (27) ».
  const regrouperPuces = () => {
    const facet = form.querySelector('details.facet--familles');
    const zone = root.querySelector('[data-facets-chips] .chips');
    if (!facet || !zone) return;
    const anciennes = [...zone.querySelectorAll('li[data-chip-filter="' + facet.getAttribute('data-facet-id') + '"]')];
    if (!anciennes.length) return;
    const icone = anciennes[0].querySelector('svg')?.outerHTML || '';
    famillesDe()
      .filter((f) => entreesFamille(facet, f).some((i) => i.checked))
      .forEach((f) => {
        const li = document.createElement('li');
        li.innerHTML = '<button type="button" class="chip" data-chip-famille="' + f.cle + '"><span></span>' + icone + '<span class="visually-hidden"></span></button>';
        li.querySelector('span').textContent = f.nom;
        li.querySelector('.visually-hidden').textContent = 'Rimuovi il filtro ' + f.nom;
        anciennes[0].before(li);
      });
    anciennes.forEach((li) => li.remove());
  };

  root.addEventListener('click', (event) => {
    const b = event.target.closest('.facet__famille, [data-chip-famille]');
    if (!b || !window.LMH?.FAMILLES) return;
    event.preventDefault();
    const facet = b.closest('details.facet') || form.querySelector('details.facet--familles');
    const cle = b.dataset.famille || b.dataset.chipFamille;
    const f = famillesDe().find((x) => x.cle === cle);
    if (!facet || !f) return;
    const entrees = entreesFamille(facet, f);
    // bouton du filtre : coche toute la couleur, ou la decoche si elle l est deja ; puce du rappel : la retire
    const cocher = b.dataset.chipFamille ? false : !(entrees.length > 0 && entrees.every((i) => i.checked));
    entrees.forEach((i) => { i.checked = cocher; });
    refresh(buildUrl(), { recaler: true });
  });

  placeForm();
  enrichirFiltres();
  regrouperPuces();
  desktop.addEventListener('change', placeForm);

  const buildUrl = () => {
    const data = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      if (String(value).trim() === '') continue;
      params.append(key, value);
    }

    params.delete('page');

    const query = params.toString();
    return window.location.pathname + (query ? `?${query}` : '');
  };

  const results = () => root.querySelector('[data-facets-results]');

  const syncMobileTabs = () => {
    if (desktop.matches || !mobileInline) return;
    const open = form.querySelector('details.facet[open]');
    const openId = open?.getAttribute('data-facet-id');
    root.querySelectorAll('[data-facet-tab]').forEach((tab) => {
      tab.setAttribute(
        'aria-selected',
        tab.getAttribute('data-facet-tab') === openId ? 'true' : 'false'
      );
    });
  };

  const refresh = async (url, { push = true, recaler = false } = {}) => {
    const container = results();
    const cle = cleFocus();
    container?.classList.add('is-loading');
    container?.setAttribute('aria-busy', 'true');

    controller?.abort();
    controller = new AbortController();

    try {
      const target = new URL(url, window.location.origin);
      const request = new URL(target);
      if (sectionId) request.searchParams.set('section_id', sectionId);

      const response = await fetch(request, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const nextResults = doc.querySelector('[data-facets-results]');
      if (nextResults && container) container.innerHTML = nextResults.innerHTML;

      const nextForm = doc.querySelector('[data-facets-form]');
      if (nextForm) {
        const openIds = new Set(
          [...form.querySelectorAll('details.facet')]
            .filter((d) => d.open)
            .map((d) => d.getAttribute('data-facet-id'))
        );
        // lmdh 30/09/2026 (rapport 334, C1) : la liste d un filtre long (Coloris, 100 valeurs) repartait
        // en haut a chaque remplacement du formulaire ; on garde sa position.
        const listes = new Map(
          [...form.querySelectorAll('details.facet')].map((d) => [
            d.getAttribute('data-facet-id'),
            d.querySelector('.facet__values')?.scrollTop || 0,
          ])
        );
        form.innerHTML = nextForm.innerHTML;
        form.querySelectorAll('details.facet').forEach((details) => {
          const id = details.getAttribute('data-facet-id');
          details.open = openIds.has(id);
          const liste = details.querySelector('.facet__values');
          if (liste && listes.get(id)) liste.scrollTop = listes.get(id);
        });
      }

      ['[data-facets-count]', '[data-facets-chips]'].forEach((selector) => {
        const next = doc.querySelector(selector);
        const current = root.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });

      placeForm();
      syncMobileTabs();
      enrichirFiltres();
      regrouperPuces();

      // lmdh 30/09/2026 (rapport 334, C1) : sur telephone, choisir « Gris » (56e valeur) envoyait la page
      // au pied de page (+2 150 px) : le panneau se referme et l ancrage de defilement suivait la ligne
      // cochee. On ramene la barre des filtres sous l en-tete, les resultats juste dessous.
      if (recaler && !desktop.matches) {
        const barre = root.querySelector('.collection-controls') || results();
        const entete =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const haut = barre ? barre.getBoundingClientRect().top : 0;
        if (barre && (haut < entete || haut > window.innerHeight * 0.5)) {
          window.scrollTo({ top: window.scrollY + haut - entete - 8, behavior: 'instant' });
        }
      }

      roles();
      rendreFocus(cle);
      annoncer();

      if (push) window.history.pushState({ facets: true }, '', target.toString());
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      container?.classList.remove('is-loading');
      container?.removeAttribute('aria-busy');
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    refresh(buildUrl());
  });

  form.addEventListener('change', () => refresh(buildUrl(), { recaler: true }));

  let priceTimer = null;
  form.addEventListener('input', (event) => {
    if (event.target.type !== 'number') return;
    window.clearTimeout(priceTimer);
    priceTimer = window.setTimeout(() => refresh(buildUrl()), 500);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-facets-sort]');
    if (!select || form.contains(select)) return;

    const inForm = form.querySelector('[data-facets-sort]');
    if (inForm) inForm.value = select.value;
    refresh(buildUrl());
  });

  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-remove], .pagination a');
    if (!link) return;
    event.preventDefault();
    refresh(link.href, { recaler: !link.matches('.pagination a') });

    if (link.matches('.pagination a')) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  window.addEventListener('popstate', () => refresh(window.location.href, { push: false }));

  /* Onglets mobiles : panneau inline sous les pills */
  root.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-facet-tab]');
    if (!tab || !root.contains(tab)) return;
    if (desktop.matches) return;

    event.preventDefault();
    const facetId = tab.getAttribute('data-facet-tab');
    const details = form.querySelector(`details.facet[data-facet-id="${facetId}"]`);
    if (!details) return;

    const already = tab.getAttribute('aria-selected') === 'true';

    form.querySelectorAll('details.facet').forEach((d) => {
      d.open = false;
    });
    root.querySelectorAll('[data-facet-tab]').forEach((t) => {
      t.setAttribute('aria-selected', 'false');
    });

    if (!already) {
      details.open = true;
      tab.setAttribute('aria-selected', 'true');
    }
  });

  /* lmdh 12/10/2026 (lot AP, U72) : onglets du telephone, fleches gauche et droite, Debut et Fin (motif des onglets) */
  root.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-facet-tab]');
    if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...root.querySelectorAll('[data-facet-tab]')].filter((t) => !t.hidden && t.getClientRects().length > 0);
    const i = tabs.indexOf(tab);
    if (i < 0) return;
    event.preventDefault();
    let j = i + (event.key === 'ArrowRight' ? 1 : -1);
    if (event.key === 'Home') j = 0;
    if (event.key === 'End') j = tabs.length - 1;
    tabs[(j + tabs.length) % tabs.length].focus();
  });
})();
